import React, { useState } from 'react'
import Link from 'next/link'
import cn from 'classnames'
import { useRouter } from 'next/router'
import Image from 'next/image'

import Button from '../newButton/Button'

import { useMediaQuery } from '@utils'
import { cards, routes, social } from './const'

import styles from './newFooter.module.scss'

const BaseFooter = () => {
  const clickHandler = () => {
    // GTM call
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'call' })
    }
    // UA call
    if (window.ga?.getAll) {
      window.ga.getAll()[0].send('event', 'call')
    }
  }

  return (
    <>
      <div className={styles.payment}>
        {cards?.map((card) => (
          <Image
            key={card.name}
            src={card.src}
            alt={`${card.name} logo`}
            width={44}
            height={28}
            quality={30}
            aria-label={`${card.name} logo`}
            role="img"
            className={styles.card}
            loading="lazy"
          />
        ))}
      </div>

      <div className={styles.contact}>
        <a
          className={styles.phoneNumber}
          href="tel:+1-888-488-7203"
          onClick={clickHandler}
        >
          <div className={styles.icon} />
          1-888-488-7203
        </a>

        <div className={styles.social}>
          {social?.map((s) => (
            <Link key={s.name} href={s.src} legacyBehavior>
              <div className={s.className} role="img" aria-label={s.label} />
            </Link>
          ))}
        </div>
      </div>

      <div className={styles.copyRight}>
        <p>© 2022 Mawoo Pets LLC. All rights reserved</p>
      </div>
    </>
  )
}

const NewsLetterSubscription = ({ isMobile }) => {
  const [email, setEmail] = useState('')
  const [hasSubscribed, setHasSubscribed] = useState(false)
  const { push } = useRouter()

  const onSubmit = (e) => {
    e?.preventDefault()

    if (email && window?._learnq) {
      window._learnq.push([
        'identify',
        {
          $email: email,
          $source: 'Blog subscriber',
        },
      ])

      setHasSubscribed(true)
    }
  }

  return (
    <div className={styles.newsletter}>
      {hasSubscribed ? (
        <>
          <img src="/footer/heart-bubble.svg" alt="heart-bubble" />
          <h3>Thanks for signing up!</h3>
          <p>
            Keep an eye on your email. We’ve got lots of amazing things coming
            your way!{' '}
          </p>
        </>
      ) : (
        <>
          <h4>Join the pack!</h4>
          <p>
            Sign up and stay up to date with lovely pups, deals, new articles,
            and more!
          </p>
          <form onSubmit={onSubmit}>
            <input
              type="email"
              placeholder="Enter email address"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <div className={styles.icon} onClick={onSubmit} />
          </form>
        </>
      )}

      {!isMobile ? (
        <div className={styles.buttons}>
          <h4>Find your puppy match!</h4>
          <Button
            theme={['green-solid', 'extra-short']}
            title="Puppy Match Quiz"
            style={{ width: '250px' }}
            onClick={() => {
              push(`${process.env.NEXT_PUBLIC_ROOT_URL}/quiz`)
            }}
          />
        </div>
      ) : null}
    </div>
  )
}

const NewFooter = () => {
  const isMobile = useMediaQuery('(max-width:600px)')

  return (
    <div className={styles.footerContainer}>
      <div className={styles.logoContainer}>
        <div
          className={styles.logo}
          role="img"
          aria-label="Mawoo Pets horizontal logo in black"
        />
        <p>Mawoo Pets is a registered entity in USA and Canada.</p>
      </div>

      <div className={styles.badges}>
        <Link
          href="https://www.bbb.org/ca/on/richmond-hill/profile/dog-breeders/friend-a-puppy-ltd-0107-1390922"
          legacyBehavior
        >
          <div className={styles.bbb} role="img" aria-label="BBB logo" />
        </Link>
      </div>

      <div className={styles.linksContainer}>
        <div className={cn(styles.linksColumn, styles.firstColumnHeight)}>
          <p>Puppies</p>
          <div className={styles.linksFind}>
            {routes?.puppies?.map(({ title, slug }) => (
              <Link key={title} href={slug}>
                {title}
              </Link>
            ))}

            {isMobile ? (
              <Link href={`${process.env.NEXT_PUBLIC_ROOT_URL}/quiz`}>
                Puppy Match Quiz
              </Link>
            ) : null}
          </div>
        </div>

        <div className={cn(styles.linksColumn, styles.secondColumnHeight)}>
          <p>Mawoo Pets</p>
          <div className={styles.links}>
            {routes?.mawooPets?.map(({ title, slug }) => (
              <Link href={slug} key={title}>
                {title}
              </Link>
            ))}
          </div>
        </div>

        <div className={cn(styles.linksColumn, styles.thirdColumnHeight)}>
          <p>Support</p>
          <div className={styles.links}>
            {routes?.support?.map(({ title, slug }) => (
              <Link key={title} href={slug}>
                {title}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <NewsLetterSubscription isMobile={isMobile} />

      <div className={styles.lineSeparation} />

      <BaseFooter />
    </div>
  )
}
export default NewFooter
